import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import FormLabel from '@mui/joy/FormLabel';
import Option from '@mui/joy/Option';
import Select from '@mui/joy/Select';

import { useState } from 'react';

import { useOrganizationId } from '@/hooks/useSwr';
import { FORMATS, TFormat, download } from '@/utils/datasets';

import { DatasetPicker } from '../DatasetPicker';
import { Pane } from '../Pane';
import useToast from '../Toast/useToast';

const defaultFormat = 'csv' as TFormat;

export function DatasetExportPane({
  open,
  onCancel,
  onSubmit,
  defaultDatasetId,
  asOf,
}: {
  open: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  defaultDatasetId?: string;
  asOf?: string;
}) {
  const organizationId = useOrganizationId();
  const [datasetId, setDatasetId] = useState<string | undefined>(
    defaultDatasetId ?? undefined
  );
  const [format, setFormat] = useState<TFormat>(defaultFormat);
  const [loading, setLoading] = useState(false);
  const { createToast } = useToast();

  return (
    <Pane
      open={open}
      onClose={onCancel}
      title="Download Dataset"
      dialogStyle={{
        marginLeft: `calc(max(4.5rem, 100vw - max(20vw, 800px)))`,
      }}
    >
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!datasetId) {
            createToast('No dataset id found');
            return;
          }

          if (!organizationId) {
            createToast('No organization found');
            return;
          }

          try {
            setLoading(true);
            await download(organizationId, datasetId, format, asOf);
          } catch (e) {
            createToast({
              title: 'Error downloading dataset',
              description: e?.toString(),
              duration: 10000,
            });
          }
          setLoading(false);
          onSubmit?.();
        }}
      >
        {defaultDatasetId ? null : (
          <DatasetPicker
            datasetId={datasetId}
            onChange={setDatasetId}
            withNewButton={false}
          />
        )}
        <FormLabel
          sx={{ marginBottom: 0.5, marginTop: defaultDatasetId ? 0 : 2 }}
        >
          Format
        </FormLabel>
        <Select
          value={format}
          onChange={(_, value) => setFormat(value || defaultFormat)}
        >
          {FORMATS.map(({ value, label }) => (
            <Option key={value} value={value}>
              {label}
            </Option>
          ))}
        </Select>
        <Box marginTop={3} display="flex" gap={1}>
          <Button
            type={'button'}
            onClick={onCancel}
            color="neutral"
            variant="soft"
          >
            Cancel
          </Button>
          <Button
            type={'submit'}
            color="primary"
            loading={loading}
            sx={{ flex: 1 }}
          >
            Download
          </Button>
        </Box>
      </form>
    </Pane>
  );
}
